import { GenData, QEq, QSingle } from '@educraft/base';
import { SimpleGenEq, SimpleGenSingle, Numerals, Common } from '../genHelper';

export const ParenthesisPlusTimes = {
  mixed:
    () =>
    (data: GenData): QEq => {
      const templates = [
        TemplateA,
        TemplateB,
        TemplateC,
        TemplateD,
        TemplateE,
        TemplateF,
        TemplateG,
        TemplateH,
        TemplateI,
        TemplateJ,
        TemplateK,
        TemplateL,
        TemplateM,
        TemplateN,
      ];
      while (true) {
        const fn = templates[data.random.next(templates.length)];
        const q = fn(data.random);
        const res = typeof q.result === 'number' ? q.result : parseFloat(String(q.result));
        if (res >= 0 && res <= 100) return q;
      }
    },
};

const TemplateA = (r: any): QEq => {
  const a = r.next(1, 11),
    b = r.next(1, 11),
    c = r.next(1, 11);
  return { text: `(${a} + ${b}) × ${c} =`, result: (a + b) * c };
};
const TemplateB = (r: any): QEq => {
  const a = r.next(1, 11),
    b = r.next(1, a + 1),
    c = r.next(1, 11);
  return { text: `(${a} - ${b}) × ${c} =`, result: (a - b) * c };
};
const TemplateC = (r: any): QEq => {
  const a = r.next(1, 51),
    b = r.next(1, 11),
    c = r.next(1, 11);
  return { text: `${a} + (${b} × ${c}) =`, result: a + b * c };
};
const TemplateD = (r: any): QEq => {
  const b = r.next(1, 11),
    c = r.next(1, 11),
    inner = b * c;
  const a = r.next(inner, inner + 51);
  return { text: `${a} - (${b} × ${c}) =`, result: a - inner };
};
const TemplateE = (r: any): QEq => {
  while (true) {
    const a = r.next(1, 11),
      b = r.next(1, 11),
      c = r.next(1, 11);
    if ((a * b) % c !== 0) continue;
    return { text: `(${a} × ${b}) : ${c} =`, result: (a * b) / c };
  }
};
const TemplateF = (r: any): QEq => {
  const a = r.next(1, 11),
    b = r.next(1, 11),
    c = r.next(1, 11);
  return { text: `${a} × (${b} + ${c}) =`, result: a * (b + c) };
};
const TemplateG = (r: any): QEq => {
  const b = r.next(1, 11),
    c = r.next(1, b + 1),
    a = r.next(1, 11);
  return { text: `${a} × (${b} - ${c}) =`, result: a * (b - c) };
};
const TemplateH = (r: any): QEq => {
  while (true) {
    const a = r.next(1, 11),
      b = r.next(1, 11),
      c = r.next(1, 11);
    if ((a + b) % c !== 0) continue;
    const res = (a + b) / c;
    if (res < 0 || res > 100) continue;
    return { text: `(${a} + ${b}) : ${c} =`, result: res };
  }
};
const TemplateI = (r: any): QEq => {
  while (true) {
    const a = r.next(1, 11),
      b = r.next(1, a + 1),
      c = r.next(1, 11);
    if ((a - b) % c !== 0) continue;
    const res = (a - b) / c;
    if (res < 0 || res > 100) continue;
    return { text: `(${a} - ${b}) : ${c} =`, result: res };
  }
};
const TemplateJ = (r: any): QEq => {
  const b = r.next(1, 11),
    a = b * r.next(1, 11),
    c = r.next(1, 51);
  return { text: `(${a} : ${b}) + ${c} =`, result: a / b + c };
};
const TemplateK = (r: any): QEq => {
  const b = r.next(1, 11),
    a = b * r.next(1, 11),
    inner = a / b;
  const c = r.next(0, inner + 1);
  return { text: `(${a} : ${b}) - ${c} =`, result: inner - c };
};
const TemplateL = (r: any): QEq => {
  const c = r.next(1, 11),
    b = c * r.next(1, 11),
    inner = b / c;
  const a = r.next(0, 51);
  return { text: `${a} + (${b} : ${c}) =`, result: a + inner };
};
const TemplateM = (r: any): QEq => {
  const c = r.next(1, 11),
    b = c * r.next(1, 11),
    inner = b / c;
  const a = r.next(inner, inner + 51);
  return { text: `${a} - (${b} : ${c}) =`, result: a - inner };
};
const TemplateN = (r: any): QEq => {
  while (true) {
    const a = r.next(1, 51),
      b = r.next(1, 51),
      c = r.next(1, 11),
      d = r.next(1, 11);
    const res = a + b - c * d;
    if (res < 0 || res > 100) continue;
    return { text: `(${a} + ${b}) - (${c} × ${d}) =`, result: res };
  }
};

// Brackets
export class Math_Brackets__PlusMinus_MultiplicationDivision extends SimpleGenEq {
  constructor() {
    super(ParenthesisPlusTimes.mixed(), 'Math_Brackets__PlusMinus_MultiplicationDivision');
  }
}

// Numbers
export class Math_Numbers_English__To10 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toEnglishString(n), 1, 10),
      'Math_Numbers_English__To10'
    );
  }
}
export class Math_Numbers_English__To20 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toEnglishString(n), 1, 20),
      'Math_Numbers_English__To20'
    );
  }
}
export class Math_Numbers_English__To100 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toEnglishString(n), 1, 100),
      'Math_Numbers_English__To100'
    );
  }
}
export class Math_Numbers_English__To1000 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toEnglishString(n), 1, 1000),
      'Math_Numbers_English__To1000'
    );
  }
}
export class Math_Numbers_English__To1000000 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toEnglishString(n), 1, 1000000),
      'Math_Numbers_English__To1000000'
    );
  }
}
export class Math_Numbers_Roman__To20 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toRomanString(n), 1, 20),
      'Math_Numbers_Roman__To20'
    );
  }
}
export class Math_Numbers_Roman__To100 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toRomanString(n), 1, 100),
      'Math_Numbers_Roman__To100'
    );
  }
}
export class Math_Numbers_Roman__To1000 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toRomanString(n), 1, 1000),
      'Math_Numbers_Roman__To1000'
    );
  }
}
export class Math_Numbers_Roman__To2000 extends SimpleGenEq {
  constructor() {
    super(
      Common.intToString((n: number) => Numerals.toRomanString(n), 1, 2000),
      'Math_Numbers_Roman__To2000'
    );
  }
}
